import React, { useState, useCallback, useRef } from 'react';
import CoilLedgerDrawer from '../components/coils/CoilLedgerDrawer';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPenToSquare, faSave, faXmark, faSpinner } from '@fortawesome/free-solid-svg-icons';
import WarehouseInventoryActions from '../components/warehouseInventory/WarehouseInventoryActions';
import WarehouseInventoryTable from '../components/warehouseInventory/WarehouseInventoryTable';
import WarehouseInventoryForm from '../components/warehouseInventory/WarehouseInventoryForm';
import WarehouseInventoryEditForm from '../components/warehouseInventory/WarehouseInventoryEditForm';
import WarehouseInventoryDetails from '../components/warehouseInventory/WarehouseInventoryDetails';
import WarehouseInventoryAdjustForm from '../components/warehouseInventory/WarehouseInventoryAdjustForm';
import WarehouseInventoryTransferForm from '../components/warehouseInventory/WarehouseInventoryTransferForm';
import WarehouseInventoryExportModal from '../components/warehouseInventory/WarehouseInventoryExportModal';
import InventoryImportModal from '../components/warehouseInventory/InventoryImportModal';
import warehouseInventoryService from '../services/warehouseInventoryService';
import ConfirmDialog from '../components/common/ConfirmDialog';
import { useMessageAlert } from '../components/common/MessageAlert';
import { useRole } from '../context/roleContext';
import { PERMISSIONS, USER_ROLES } from '../utils/constants';
import { UI_COLORS } from '../utils/colors';
import useWarehouseInventory from '../hooks/useWarehouseInventory';
import productCatalogueService from '../services/productCatalogueService';
import { useTranslation } from 'react-i18next';

// Row expansion is keyed the same way the table keys it (product_size_id, falling
// back to id) so `expandedProducts.has(key)` lines up with what the table asks for.
const rowExpandKey = (item) => item?.product_size_id ?? item?.id;

const WarehouseInventoryPage = () => {
  const { t } = useTranslation();
  const {
    inventories,
    loading,
    totalPages,
    totalCount,
    fetchInventory,
    createInventory,
    updateInventory,
    deleteInventory,
    adjustStock,
    transferStock,
    exportBySubcategory,
  } = useWarehouseInventory();

  const [refreshLoading, setRefreshLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [showEditForm, setShowEditForm] = useState(false);
  const [editingProductId, setEditingProductId] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [committedSearch, setCommittedSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const searchDebounceRef = useRef(null);
  const PAGE_LIMIT = 20;
  const [showDetails, setShowDetails] = useState(false);
  // eslint-disable-next-line
  const [viewingInventoryId, setViewingInventoryId] = useState(null);
  const [inventoryDetails, setInventoryDetails] = useState(null);
  const [showAdjustForm, setShowAdjustForm] = useState(false);
  const [adjustingInventory, setAdjustingInventory] = useState(null);
  const [showTransferForm, setShowTransferForm] = useState(false);
  // details queue removed — we won't auto-open details after transfer
  const [errorDialog, setErrorDialog] = useState(null);

  // Filter states
  const [warehouseFilter, setWarehouseFilter] = useState('');
  const [productFilter, setProductFilter] = useState('');
  const [lowStockFilter, setLowStockFilter] = useState('all');
  // Sub Category (product catalogue) filter — array of catalogue ids.
  // Empty array = all sub categories (default). Applied server-side.
  const [subCategoryFilter, setSubCategoryFilter] = useState([]);
  const [subCategoryOptions, setSubCategoryOptions] = useState([]);
  const [subCategoriesLoading, setSubCategoriesLoading] = useState(false);

  // Bulk edit states
  const [bulkEditMode, setBulkEditMode] = useState(false);
  const [expandedProducts, setExpandedProducts] = useState(() => new Set());
  const [bulkEdits, setBulkEdits] = useState({}); // { [product_size_id]: { [warehouse_id]: { field: value } } }
  const [bulkSaving, setBulkSaving] = useState(false);
  const [bulkProgress, setBulkProgress] = useState({ current: 0, total: 0 });
  // `bulkEdits` survives paging/searching, but `inventories` only ever holds the
  // current page — so remember every row seen while bulk editing. Without this, an
  // edit made on an earlier page has no product to resolve against and is silently
  // dropped at save time (and mis-counted in the dirty summary).
  const bulkProductCacheRef = useRef(new Map());

  const { showSuccess, showError } = useMessageAlert();
  const { checkPermission, isSuperAdmin, userRole } = useRole();
  // Write access to inventory. Roles with only VIEW_INVENTORY (Sales Exec,
  // Delivery Supervisor) get a read-only screen — no add/edit/transfer/bulk-edit.
  const canManageInventory = checkPermission(PERMISSIONS.MANAGE_INVENTORY);
  // Bulk edit is also open to Operations Manager. Gated on the ROLE rather than
  // MANAGE_INVENTORY: OM's permissions are a computed union of the read-only roles, so
  // granting that permission would unlock Add / Transfer / Import for them too — and
  // would hand it to Sales Staff and Delivery Supervisor as well. The API already
  // allows OM here (inventory_access_roles_write).
  const canBulkEdit =
    canManageInventory || userRole === USER_ROLES.OPERATIONS_MANAGER;
  // Super Admin and Operations Manager. Gated on the ROLE rather than a permission
  // because Operations Manager's permissions are computed as a union of the read-only
  // roles — it has no export permission of its own to check, and adding one would grant
  // this to Sales/Delivery too. The API enforces the same two roles.
  const canExportBySubcategory =
    isSuperAdmin() || userRole === USER_ROLES.OPERATIONS_MANAGER;
  const bulkStrings = t('warehouseInventory.bulkEdit', { returnObjects: true });

  // Export by Sub Category (Super Admin only)
  const [showExportModal, setShowExportModal] = useState(false);
  // The coil ledger for one SKU. Coil stock starts empty and is entered by hand,
  // so this drawer is where coils first come into existence.
  const [coilLedgerTarget, setCoilLedgerTarget] = useState(null);

  // A products-view row is aggregated ACROSS warehouses — it carries a
  // `warehouse_breakdown[]`, never a single `warehouse_id`. Coils live at one warehouse,
  // so the ledger has to be told which: the warehouse currently filtered when there is
  // one, otherwise the row's only warehouse, otherwise the one actually holding stock.
  const resolveCoilLedgerTarget = useCallback((item) => {
    const breakdown = Array.isArray(item?.warehouse_breakdown) ? item.warehouse_breakdown : [];
    const filtered = warehouseFilter
      ? breakdown.find(w => String(w.warehouse_id) === String(warehouseFilter)
          || w.warehouse_name === warehouseFilter)
      : null;
    const chosen = filtered
      || (breakdown.length === 1 ? breakdown[0] : null)
      || breakdown.find(w => Number(w.stock_quantity) > 0)
      || breakdown[0]
      || null;
    return {
      ...item,
      warehouse_id: chosen?.warehouse_id ?? null,
      warehouse_name: chosen?.warehouse_name ?? null,
    };
  }, [warehouseFilter]);
  const [exportLoading, setExportLoading] = useState(false);

  const loadInventory = useCallback(async (isRefresh = false) => {
    if (isRefresh) setRefreshLoading(true);
    try {
      const skip = (currentPage - 1) * PAGE_LIMIT;
      await fetchInventory(
        {
          skip,
          limit: PAGE_LIMIT,
          ...(committedSearch ? { search: committedSearch } : {}),
          ...(subCategoryFilter.length > 0 ? { catalogue_ids: subCategoryFilter } : {}),
        },
        { force: isRefresh }
      );
    } catch (err) {
      console.error('Failed to load inventory', err);
    } finally {
      if (isRefresh) setRefreshLoading(false);
    }
  }, [fetchInventory, currentPage, committedSearch, subCategoryFilter]);

  // Load Sub Category (product catalogue) options for the filter once.
  React.useEffect(() => {
    let cancelled = false;
    const loadSubCategories = async () => {
      setSubCategoriesLoading(true);
      try {
        const result = await productCatalogueService.getAllProducts();
        if (cancelled) return;
        const list = Array.isArray(result?.data) ? result.data : [];
        setSubCategoryOptions(
          list.map((sc) => ({
            value: sc.id,
            label: sc.product_name || sc.name || sc.alias_name || t('bulkInventoryEdit.fallbacks.subCategory', { id: sc.id }),
          }))
        );
      } catch (e) {
        if (!cancelled) setSubCategoryOptions([]);
      } finally {
        if (!cancelled) setSubCategoriesLoading(false);
      }
    };
    loadSubCategories();
    return () => { cancelled = true; };
  }, []);

  // Reset to first page whenever the sub category filter changes.
  const handleSubCategoryFilterChange = React.useCallback((ids) => {
    setSubCategoryFilter(Array.isArray(ids) ? ids : []);
    setCurrentPage(1);
  }, []);

  React.useEffect(() => {
    loadInventory();
  }, [loadInventory]);

  const handleSearchChange = (value) => {
    setSearchQuery(value);
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      setCommittedSearch(value);
      setCurrentPage(1);
    }, 400);
  };

  const handleRefresh = () => {
    loadInventory(true);
  };

  const handleAdd = () => {
    setEditing(null);
    setShowForm(true);
  };

  // --- opening-stock import -------------------------------------------------------
  const [showImportModal, setShowImportModal] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importError, setImportError] = useState(null);
  const [importSuccess, setImportSuccess] = useState(false);

  const handleImportInventory = async (file) => {
    setImporting(true);
    setImportError(null);
    setImportSuccess(false);
    try {
      const result = await warehouseInventoryService.importInventory(file);
      if (result.success) {
        setImportSuccess(true);
        // Stock moved, so the list on screen is stale.
        loadInventory(true);
        // Say what the file actually DID. Rows that restate what is already recorded —
        // an export sent straight back, say — change nothing, and a bare "Import
        // successful" made that indistinguishable from an import that worked.
        const d = result.data || {};
        const counts = {
          balances: d.balances_set || 0,
          coils: d.coils_created || 0,
          minimums: d.minimums_set || 0,
          mappings: d.new_mappings || 0,
        };
        const changed = counts.balances + counts.coils + counts.minimums + counts.mappings;
        showSuccess(
          changed === 0
            ? t('warehouseInventory.import.noChanges')
            : t('warehouseInventory.import.success', counts),
        );
        return { success: true };
      }
      // The endpoint is all-or-nothing: on refusal nothing was written, and `error` is
      // the line-numbered list the modal renders.
      setImportError(result.error);
      return { success: false, error: result.error };
    } finally {
      setImporting(false);
    }
  };

  const handleTransfer = () => {
    setShowTransferForm(true);
  };

  const handleExportBySubcategory = async ({ catalogue_ids, warehouse_ids, value }) => {
    setExportLoading(true);
    try {
      const res = await exportBySubcategory({ catalogue_ids, warehouse_ids, value });
      if (res && res.success) {
        setShowExportModal(false);
        showSuccess(t('warehouseInventory.exportBySubcategory.success'));
      } else {
        showError(res?.error || t('warehouseInventory.exportBySubcategory.error'));
      }
      return res;
    } catch (err) {
      showError(err?.message || t('warehouseInventory.exportBySubcategory.error'));
      return { success: false, error: err?.message };
    } finally {
      setExportLoading(false);
    }
  };

  const handleEdit = (id) => {
    // Try to match by inventory `id` first, then fallback to `product_size_id`.
    const inventoryItemById = inventories.find(inv => String(inv.id) === String(id));
    const inventoryItemByProduct = inventories.find(inv => String(inv.product_size_id) === String(id));
    const inventoryItem = inventoryItemById || inventoryItemByProduct || null;
    if (inventoryItem && inventoryItem.product_size_id) {
      // Use the new distribution edit form for editing product warehouse distribution
      setEditingProductId(inventoryItem.product_size_id);
      setShowEditForm(true);
    } else {
      // Fallback to the old form if needed
      setEditing({ id });
      setShowForm(true);
    }
  };

  const handleView = async (product_size_id) => {
    // Use the stored inventories (from getAllInventory) and show the selected item
    const inventoryItem = inventories.find((inv) => String(inv.product_size_id) === String(product_size_id)) || null;
    if (inventoryItem) {
      // The details component expects either a warehouse-summary or a single-inventory-item shape.
      // We pass the inventory item directly (it should include product_details and warehouse_details when available).
      setInventoryDetails(inventoryItem);
      setViewingInventoryId(product_size_id);
      setShowDetails(true);
    } else {
      // Fallback: if item not found in stored list, optionally try fetching full inventory again
      try {
        await fetchInventory({ skip: (currentPage - 1) * PAGE_LIMIT, limit: PAGE_LIMIT }, { force: true });
        const refreshed = inventories.find((inv) => inv.product_size_id === product_size_id) || null;
        if (refreshed) {
          setInventoryDetails(refreshed);
          setViewingInventoryId(product_size_id);
          setShowDetails(true);
        }
      } catch (err) {
        console.error('Error locating inventory item for details view', err);
      }
    }
  };

  const handleDelete = (id) => {
    const item = inventories.find((inv) => inv.id === id) || null;
    const itemName = item?.product_name || t('bulkInventoryEdit.fallbacks.inventory', { id: String(id) });
    setDeleteConfirm({
      id,
      item,
      title: t('bulkInventoryEdit.dialogs.deleteInventoryTitle'),
      message: t('bulkInventoryEdit.dialogs.deleteInventoryMessage', { name: itemName }),
      confirmLabel: t('common.actions.delete'),
      cancelLabel: t('common.actions.cancel'),
      confirmVariant: 'danger'
    });
  };

  const handleAdjust = (id) => {
    const item = inventories.find((inv) => inv.id === id) || null;
    if (item) {
      setAdjustingInventory(item);
      setShowAdjustForm(true);
    }
  };

  const confirmDelete = async () => {
    if (!deleteConfirm) return;
    try {
      const res = await deleteInventory(deleteConfirm.id);
      setDeleteConfirm(null);
      if (!res || !res.success) {
        setErrorDialog({ title: t('bulkInventoryEdit.dialogs.deleteFailedTitle'), message: res?.error || t('bulkInventoryEdit.dialogs.deleteFailedMessage') });
      }
    } catch (err) {
      setDeleteConfirm(null);
      setErrorDialog({ title: t('bulkInventoryEdit.dialogs.deleteFailedTitle'), message: err?.message || t('bulkInventoryEdit.dialogs.deleteFailedMessage') });
    }
  };

  const handleFormSubmit = async (payload) => {
    try {
      let res;
      if (editing && editing.id) {
        // updateInventory expects a product_size_id (bulk endpoint). The form payload contains product_size_id.
        const prodId = payload?.product_size_id ?? null;
        res = await updateInventory(prodId, payload);
      } else {
        res = await createInventory(payload);
      }
      if (res && res.success) {
        setShowForm(false);
        setEditing(null);
        return { success: true };
      }
      // show backend error
      setErrorDialog({ title: t('bulkInventoryEdit.dialogs.saveFailedTitle'), message: res?.error || t('bulkInventoryEdit.dialogs.saveFailedMessage') });
      return res;
    } catch (err) {
      console.error('Form submit failed', err);
      setErrorDialog({ title: t('bulkInventoryEdit.dialogs.saveFailedTitle'), message: err?.message || t('bulkInventoryEdit.dialogs.saveFailedMessage') });
      return { success: false, error: err.message };
    }
  };

  const handleAdjustSubmit = async (payload) => {
    try {
      let res = { success: true };
      if (adjustingInventory && adjustingInventory.id) {
        res = await adjustStock(adjustingInventory.id, payload);
      }
      if (res && res.success) {
        setShowAdjustForm(false);
        setAdjustingInventory(null);
        return { success: true };
      }
      setErrorDialog({ title: t('bulkInventoryEdit.dialogs.adjustFailedTitle'), message: res?.error || t('bulkInventoryEdit.dialogs.adjustFailedMessage') });
      return res;
    } catch (err) {
      console.error('Adjust stock failed', err);
      setErrorDialog({ title: t('bulkInventoryEdit.dialogs.adjustFailedTitle'), message: err?.message || t('bulkInventoryEdit.dialogs.adjustFailedMessage') });
      return { success: false, error: err.message };
    }
  };

  const handleEditFormSubmit = async (payload) => {
    try {
      const res = await updateInventory(payload.product_size_id, payload);
      if (res && res.success) {
        // If API returned partial errors in a 200 response, show them to the user
        const errors = (res.data && res.data.errors) || res.errors || [];
        if (Array.isArray(errors) && errors.length > 0) {
          // Helper: resolve warehouse id to a friendly name using available data
          const getWarehouseName = (wid) => {
            if (wid == null) return t('common.messages.unknownWarehouse');
            const idStr = String(wid);
            // look in current inventories
            const foundInv = (Array.isArray(inventories) ? inventories : []).find(i => String(i.warehouse_id) === idStr || String(i.warehouse_details?.id) === idStr);
            if (foundInv) return foundInv.warehouse_details?.name || foundInv.warehouse_name;
            // try warehouseOptions if available
            if (Array.isArray(warehouseOptions)) {
              const opt = warehouseOptions.find(o => String(o.value) === idStr);
              if (opt) return opt.label;
            }
            // try global cached options used by WarehouseInventoryForm
            try {
              const cached = window?.warehouseInventoryFormCache?.warehouses || null;
              if (Array.isArray(cached)) {
                const found = cached.find(o => String(o.value) === idStr);
                if (found) return found.label;
              }
            } catch (e) {}
            return t('common.messages.unknownWarehouse');
          };

          // Build a concise message listing each failure using warehouse names
          const lines = errors.map((err) => {
            const wid = err.warehouse_data?.warehouse_id;
            const name = getWarehouseName(wid);
            const msg = err.error || JSON.stringify(err);
            return `${name}: ${msg}`;
          });
          setErrorDialog({ title: t('bulkInventoryEdit.dialogs.partialUpdateErrorsTitle'), message: lines.join('\n') });
        }
        // Refresh local inventory list to reflect changes and update details view
        try {
          const refreshed = await fetchInventory({ skip: (currentPage - 1) * PAGE_LIMIT, limit: PAGE_LIMIT, ...(committedSearch ? { search: committedSearch } : {}) }, { force: true });
          // Extract items array from possible response shapes
          let items = [];
          if (refreshed && Array.isArray(refreshed.data)) items = refreshed.data;
          else if (refreshed && refreshed.data && Array.isArray(refreshed.data.items)) items = refreshed.data.items;
          else items = Array.isArray(inventories) ? inventories : [];

          const updated = items.find((inv) => String(inv.product_size_id) === String(payload.product_size_id)) || null;
          if (updated) {
            setInventoryDetails(updated);
          }
        } catch (e) {
          // If refresh fails, attempt to set from current local list
          const updatedFallback = inventories.find((inv) => String(inv.product_size_id) === String(payload.product_size_id)) || null;
          if (updatedFallback) setInventoryDetails(updatedFallback);
        }

        setShowEditForm(false);
        setEditingProductId(null);
        return { success: true };
      }
      setErrorDialog({ title: t('bulkInventoryEdit.dialogs.updateFailedTitle'), message: res?.error || t('bulkInventoryEdit.dialogs.updateFailedMessage') });
      return res;
    } catch (err) {
      console.error('Edit form submit failed', err);
      setErrorDialog({ title: t('bulkInventoryEdit.dialogs.updateFailedTitle'), message: err?.message || t('bulkInventoryEdit.dialogs.updateFailedMessage') });
      return { success: false, error: err.message };
    }
  };

  const handleTransferSubmit = async (payload) => {
    try {
      const res = await transferStock(payload);
      // If transfer succeeded, refresh inventory. Do NOT open details modal.
      if (res && res.success) {
        loadInventory(true);
        setShowTransferForm(false);
      } else {
        // show backend error
        setErrorDialog({ title: t('bulkInventoryEdit.dialogs.transferFailedTitle'), message: res?.error || t('bulkInventoryEdit.dialogs.transferFailedMessage') });
      }
      return res;
    } catch (err) {
      console.error('Transfer stock failed', err);
      setErrorDialog({ title: t('bulkInventoryEdit.dialogs.transferFailedTitle'), message: err?.message || t('bulkInventoryEdit.dialogs.transferFailedMessage') });
      return { success: false, error: err.message };
    }
  };

  // Warehouse options for filters - derive from inventories already loaded
  const warehouseOptions = React.useMemo(() => {
    if (!Array.isArray(inventories)) return [];
    const map = new Map();
    inventories.forEach((inv) => {
      const id = inv.warehouse_id != null ? String(inv.warehouse_id) : null;
      if (!id) return;
      if (!map.has(id)) {
        const name = inv.warehouse_details?.name || inv.warehouse_name || t('bulkInventoryEdit.fallbacks.warehouse', { id: String(id) });
        map.set(id, { value: id, label: name });
      }
    });
    return Array.from(map.values());
  }, [inventories]);

  // Filter inventory items (local filters only — search is server-side)
  const filtered = React.useMemo(() => {
    let result = inventories;

    // Warehouse filter - match by warehouse name or ID in warehouse_breakdown
    if (warehouseFilter) {
      result = result.filter((inv) => {
        // Check if warehouse filter matches top-level warehouse data
        if (String(inv.warehouse_id) === warehouseFilter || 
            (inv.warehouse_name && inv.warehouse_name === warehouseFilter)) {
          return true;
        }
        
        // Check if any warehouse in warehouse_breakdown matches
        if (Array.isArray(inv.warehouse_breakdown)) {
          return inv.warehouse_breakdown.some(w => 
            String(w.warehouse_id) === warehouseFilter || 
            w.warehouse_name === warehouseFilter
          );
        }
        
        return false;
      });
    }

    // Product filter
    if (productFilter.trim()) {
      const productTerm = productFilter.toLowerCase();
      result = result.filter((inv) => 
        (inv.product_name || '').toLowerCase().includes(productTerm)
      );
    }

    // Low stock filter - check warehouse_breakdown for is_low_stock
    if (lowStockFilter === 'yes') {
      result = result.filter((inv) => {
        if (Array.isArray(inv.warehouse_breakdown)) {
          return inv.warehouse_breakdown.some(w => w.is_low_stock === true);
        }
        return inv.is_low_stock === true;
      });
    } else if (lowStockFilter === 'no') {
      result = result.filter((inv) => {
        if (Array.isArray(inv.warehouse_breakdown)) {
          return !inv.warehouse_breakdown.some(w => w.is_low_stock === true);
        }
        return inv.is_low_stock !== true;
      });
    }

    // Warehouse-scoped totals, row visibility, and pagination are handled by the
    // backend (products-view honors warehouse_ids), so the totals and rows the
    // API returns already reflect the selected warehouses — no FE recompute.
    return result;
  }, [inventories, warehouseFilter, productFilter, lowStockFilter]);

  // ---------- Bulk Edit helpers ----------
  // A cell is "dirty" when the user's edited value differs from the original stored value.
  // An empty string is considered "no change" (mid-typing / cleared).
  const isCellDirty = (editedValue, originalValue) => {
    if (editedValue === undefined || editedValue === null || editedValue === '') return false;
    const editedNum = Number(editedValue);
    const origNum = Number(originalValue ?? 0);
    if (Number.isFinite(editedNum) && Number.isFinite(origNum)) return editedNum !== origNum;
    return String(editedValue) !== String(originalValue ?? '');
  };

  const dirtySummary = React.useMemo(() => {
    let cellCount = 0;
    let productCount = 0;
    let hiddenProductCount = 0;
    const visibleIds = new Set((filtered || []).map((p) => String(p.product_size_id)));
    const inventoryById = new Map((inventories || []).map((p) => [String(p.product_size_id), p]));

    Object.keys(bulkEdits).forEach((prodId) => {
      const warehouses = bulkEdits[prodId] || {};
      // Fall back to the cache for products edited on a page we have since left,
      // otherwise their originals read as 0 and unchanged cells count as dirty.
      const product = inventoryById.get(String(prodId)) || bulkProductCacheRef.current.get(String(prodId));
      const breakdown = Array.isArray(product?.warehouse_breakdown) ? product.warehouse_breakdown : [];
      let productHasDirty = false;
      Object.keys(warehouses).forEach((whId) => {
        const fields = warehouses[whId] || {};
        const orig = breakdown.find((w) => String(w.warehouse_id || w.id) === String(whId)) || {};
        Object.keys(fields).forEach((fName) => {
          if (isCellDirty(fields[fName], orig[fName])) {
            cellCount += 1;
            productHasDirty = true;
          }
        });
      });
      if (productHasDirty) {
        productCount += 1;
        if (!visibleIds.has(String(prodId))) hiddenProductCount += 1;
      }
    });
    return { cellCount, productCount, hiddenProductCount };
  }, [bulkEdits, filtered, inventories]);

  const handleToggleBulkMode = () => {
    if (bulkEditMode && dirtySummary.cellCount > 0) {
      // Confirm discard
      setErrorDialog({
        title: bulkStrings.discardTitle,
        message: bulkStrings.discardMessage,
        _confirmDiscard: true,
      });
      return;
    }
    const entering = !bulkEditMode;
    setBulkEditMode(entering);
    setBulkEdits({});
    bulkProductCacheRef.current = new Map();
    // Entering bulk edit expands every product up front — the whole point of the
    // mode is editing many warehouse rows, so nobody should have to click each row.
    setExpandedProducts(
      entering
        ? new Set((filtered || []).map(rowExpandKey).filter((key) => key !== undefined && key !== null))
        : new Set()
    );
  };

  // Remember each page's rows for as long as bulk edit is on, so edits made before
  // paging/searching can still be matched to their product at save time.
  React.useEffect(() => {
    if (!bulkEditMode) return;
    (inventories || []).forEach((item) => {
      const key = rowExpandKey(item);
      if (key !== undefined && key !== null) {
        bulkProductCacheRef.current.set(String(key), item);
      }
    });
  }, [bulkEditMode, inventories]);

  // Rows that arrive later (page change, refresh, filter change) come in collapsed,
  // so expand them too while bulk edit is on. Only ever adds, so a row the user
  // collapsed by hand stays collapsed until the row set itself changes.
  React.useEffect(() => {
    if (!bulkEditMode) return;
    setExpandedProducts((prev) => {
      const next = new Set(prev);
      let added = false;
      (filtered || []).forEach((item) => {
        const key = rowExpandKey(item);
        if (key !== undefined && key !== null && !next.has(key)) {
          next.add(key);
          added = true;
        }
      });
      return added ? next : prev;
    });
  }, [bulkEditMode, filtered]);

  const handleToggleExpand = (productSizeId) => {
    setExpandedProducts((prev) => {
      const next = new Set(prev);
      if (next.has(productSizeId)) next.delete(productSizeId);
      else next.add(productSizeId);
      return next;
    });
  };

  const handleBulkCellChange = (productSizeId, warehouseId, field, value) => {
    // Always store the raw user input (including "" and "0") so the input stays controlled by user intent.
    // Cleaning up empty strings happens at save time and in dirtySummary, not here.
    setBulkEdits((prev) => {
      const nextProduct = { ...(prev[productSizeId] || {}) };
      const nextWarehouse = { ...(nextProduct[warehouseId] || {}) };
      nextWarehouse[field] = value == null ? '' : String(value);
      nextProduct[warehouseId] = nextWarehouse;
      const next = { ...prev };
      next[productSizeId] = nextProduct;
      return next;
    });
  };

  const handleDiscardBulk = () => {
    setBulkEdits({});
    setExpandedProducts(new Set());
    setBulkEditMode(false);
    bulkProductCacheRef.current = new Map();
  };

  const handleBulkSave = async () => {
    const safeNum = (v, fallback = 0) => {
      const n = Number(v);
      return Number.isFinite(n) ? n : fallback;
    };

    // Build save plan: only include products with at least one truly dirty warehouse field.
    // For each dirty warehouse we send ALL three fields (edited or original) since backend expects full state.
    const savePlan = [];
    const cellCountByProduct = {};

    Object.keys(bulkEdits).forEach((prodId) => {
      // Resolve from the current page first, then from the rows seen earlier in this
      // bulk-edit session — otherwise edits made before paging/searching are dropped
      // here without a word and linger in the footer count after a "successful" save.
      const product = inventories.find((inv) => String(inv.product_size_id) === String(prodId))
        || bulkProductCacheRef.current.get(String(prodId));
      if (!product) return;
      const breakdown = Array.isArray(product?.warehouse_breakdown) ? product.warehouse_breakdown : [];
      const warehouseEdits = bulkEdits[prodId] || {};

      const warehouse_updates = [];
      let productCellCount = 0;

      Object.keys(warehouseEdits).forEach((whId) => {
        const fields = warehouseEdits[whId] || {};
        const orig = breakdown.find((w) => String(w.warehouse_id || w.id) === String(whId)) || {};

        const dirtyFields = ['stock_quantity', 'minimum_stock', 'pending_order'].filter(
          (f) => isCellDirty(fields[f], orig[f])
        );
        if (dirtyFields.length === 0) return;
        productCellCount += dirtyFields.length;

        const resolve = (field) => {
          const edited = fields[field];
          if (edited === undefined || edited === '' || edited === null) return safeNum(orig[field]);
          return safeNum(edited, safeNum(orig[field]));
        };

        warehouse_updates.push({
          warehouse_id: safeNum(whId),
          stock_quantity: resolve('stock_quantity'),
          minimum_stock: resolve('minimum_stock'),
          pending_order: resolve('pending_order'),
        });
      });

      if (warehouse_updates.length > 0) {
        savePlan.push({ prodId, product, warehouse_updates });
        cellCountByProduct[prodId] = productCellCount;
      }
    });

    if (savePlan.length === 0) return;

    setBulkSaving(true);
    setBulkProgress({ current: 0, total: savePlan.length });

    try {
      const failures = [];
      const succeededProductIds = [];

      for (let i = 0; i < savePlan.length; i += 1) {
        const { prodId, product, warehouse_updates } = savePlan[i];
        setBulkProgress({ current: i + 1, total: savePlan.length });

        try {
          const res = await updateInventory(Number(prodId), { product_size_id: Number(prodId), warehouse_updates });
          if (res && res.success) {
            const partial = (res.data && res.data.errors) || res.errors || [];
            if (Array.isArray(partial) && partial.length > 0) {
              failures.push({ productId: prodId, productName: product?.product_name || t('bulkInventoryEdit.fallbacks.product', { id: prodId }), errors: partial });
            } else {
              succeededProductIds.push(prodId);
            }
          } else {
            failures.push({ productId: prodId, productName: product?.product_name || t('bulkInventoryEdit.fallbacks.product', { id: prodId }), errors: [{ error: res?.error || t('common.messages.updateFailed') }] });
          }
        } catch (err) {
          failures.push({ productId: prodId, productName: product?.product_name || t('bulkInventoryEdit.fallbacks.product', { id: prodId }), errors: [{ error: err?.message || t('common.messages.networkError') }] });
        }
      }

      // Clear edits for products that saved cleanly
      setBulkEdits((prev) => {
        const next = { ...prev };
        succeededProductIds.forEach((id) => { delete next[id]; });
        return next;
      });

      // Refresh inventory data
      try { await fetchInventory({ skip: (currentPage - 1) * PAGE_LIMIT, limit: PAGE_LIMIT, ...(committedSearch ? { search: committedSearch } : {}) }, { force: true }); } catch (e) { /* noop */ }

      const savedCellCount = succeededProductIds.reduce((sum, pid) => sum + (cellCountByProduct[pid] || 0), 0);

      if (failures.length === 0) {
        showSuccess(
          (bulkStrings.allSuccess)
            .replace('{count}', String(savedCellCount))
            .replace('{products}', String(succeededProductIds.length))
        );
      } else if (succeededProductIds.length === 0) {
        showError(bulkStrings.allFailed);
        const lines = failures.map((f) => `${f.productName}: ${(f.errors[0]?.error) || t('common.messages.unknownError')}`);
        setErrorDialog({ title: t('bulkInventoryEdit.dialogs.saveErrorsTitle'), message: lines.join('\n') });
      } else {
        const lines = failures.map((f) => `${f.productName}: ${(f.errors[0]?.error) || t('common.messages.unknownError')}`);
        setErrorDialog({
          title: bulkStrings.partialFailureTitle,
          message: (bulkStrings.partialFailureBody)
            .replace('{succeeded}', String(succeededProductIds.length))
            .replace('{total}', String(savePlan.length)) + lines.join('\n'),
        });
      }
    } catch (err) {
      console.error('Bulk save failed', err);
      setErrorDialog({ title: t('bulkInventoryEdit.dialogs.saveErrorsTitle'), message: err?.message || t('common.messages.unknownError') });
    } finally {
      setBulkSaving(false);
      setBulkProgress({ current: 0, total: 0 });
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-3 sm:p-6 w-full max-w-full">
      <div className="mb-8">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">
              {t('warehouseInventory.page.header.title')}
            </h1>
            <p className="mt-2 text-gray-600">
                {t('warehouseInventory.page.header.subtitle')}
            </p>
          </div>

          <div className="flex items-center gap-3 flex-wrap">
            {canBulkEdit && (
              <button
                onClick={handleToggleBulkMode}
                disabled={bulkSaving}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                style={{
                  backgroundColor: bulkEditMode ? UI_COLORS.STATUS.WARNING : UI_COLORS.BUTTONS.PRIMARY.background,
                  color: bulkEditMode ? '#fff' : UI_COLORS.BUTTONS.PRIMARY.text,
                }}
              >
                <FontAwesomeIcon icon={bulkEditMode ? faXmark : faPenToSquare} />
                {bulkEditMode ? bulkStrings.exitMode : bulkStrings.enterMode}
              </button>
            )}
            <div className="bg-white px-4 py-2 rounded-lg border border-gray-200">
              <div className="flex items-center space-x-3">
                <div className="text-sm text-gray-500">
                  {t('warehouseInventory.page.stats.total')}
                </div>
                <div className="text-2xl font-bold text-gray-900">
                  {totalCount || (Array.isArray(inventories) ? inventories.length : 0)}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <WarehouseInventoryActions
        searchValue={searchQuery}
        onSearchChange={handleSearchChange}
        warehouseFilterValue={warehouseFilter}
        onWarehouseFilterChange={setWarehouseFilter}
        productFilterValue={productFilter}
        onProductFilterChange={setProductFilter}
        lowStockFilterValue={lowStockFilter}
        onLowStockFilterChange={setLowStockFilter}
        subCategoryFilterValue={subCategoryFilter}
        onSubCategoryFilterChange={handleSubCategoryFilterChange}
        subCategoryOptions={subCategoryOptions}
        subCategoriesLoading={subCategoriesLoading}
        onAddInventory={canManageInventory ? handleAdd : undefined}
        onTransferStock={canManageInventory ? handleTransfer : undefined}
        onImportInventory={canManageInventory ? () => setShowImportModal(true) : undefined}
        onRefresh={handleRefresh}
        onExportBySubcategory={canExportBySubcategory ? () => setShowExportModal(true) : undefined}
        exportLoading={exportLoading}
        loading={loading}
        refreshLoading={refreshLoading}
        warehouseOptions={warehouseOptions}
        warehouseInventory={inventories}
      />

      <div className="bg-white shadow-sm rounded-lg border border-gray-200 mt-6">
        <WarehouseInventoryTable
          inventoryItems={filtered}
          loading={loading}
          onViewInventory={handleView}
          onEditInventory={handleEdit}
          onAdjustInventory={handleAdjust}
          onOpenCoilLedger={(item) => setCoilLedgerTarget(resolveCoilLedgerTarget(item))}
          onDeleteInventory={handleDelete}
          bulkEditMode={bulkEditMode}
          expandedProducts={expandedProducts}
          onToggleExpand={handleToggleExpand}
          bulkEdits={bulkEdits}
          onBulkCellChange={handleBulkCellChange}
        />

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="mt-4 flex items-center justify-between rounded-xl border border-gray-200 bg-white px-6 py-3 mx-4 mb-4">
            <p className="text-sm text-gray-500">
              {t('bulkInventoryEdit.pagination.pageOfTotal', { current: String(currentPage), total: String(totalPages), count: String(totalCount) })}
            </p>
            <div className="flex items-center gap-1">
              <button
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1 || loading}
                className="px-3 py-1.5 rounded-lg text-sm font-medium border border-gray-200 disabled:opacity-40 hover:bg-gray-50"
              >
                {t('bulkInventoryEdit.pagination.previous')}
              </button>
              {(() => {
                const pages = [];
                const delta = 2;
                const left = Math.max(2, currentPage - delta);
                const right = Math.min(totalPages - 1, currentPage + delta);
                pages.push(1);
                if (left > 2) pages.push('...');
                for (let i = left; i <= right; i++) pages.push(i);
                if (right < totalPages - 1) pages.push('...');
                if (totalPages > 1) pages.push(totalPages);
                return pages.map((p, i) =>
                  p === '...' ? (
                    <span key={`ellipsis-${i}`} className="px-2 py-1.5 text-sm text-gray-400">…</span>
                  ) : (
                    <button
                      key={p}
                      onClick={() => setCurrentPage(p)}
                      disabled={loading}
                      className="px-3 py-1.5 rounded-lg text-sm font-medium border"
                      style={currentPage === p
                        ? { backgroundColor: '#0066cc', color: '#fff', borderColor: '#0066cc' }
                        : { borderColor: '#e5e7eb', color: '#374151' }
                      }
                    >
                      {p}
                    </button>
                  )
                );
              })()}
              <button
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages || loading}
                className="px-3 py-1.5 rounded-lg text-sm font-medium border border-gray-200 disabled:opacity-40 hover:bg-gray-50"
              >
                {t('bulkInventoryEdit.pagination.next')}
              </button>
            </div>
          </div>
        )}
      </div>

      {showForm && (
          <WarehouseInventoryForm 
            isOpen={showForm}
            inventoryId={editing?.id || null}
            onSubmit={handleFormSubmit} 
            onCancel={() => { setShowForm(false); setEditing(null); }} 
          />
      )}

      {showEditForm && (
        <WarehouseInventoryEditForm
          isOpen={showEditForm}
          productSizeId={editingProductId}
          inventories={inventories}
          inventoryLoading={loading}
          onSubmit={handleEditFormSubmit}
          onClose={() => {
            setShowEditForm(false);
            setEditingProductId(null);
          }}
          loading={loading}
        />
      )}

      {showDetails && (
        <WarehouseInventoryDetails
          isOpen={showDetails}
          inventory={inventoryDetails}
          onClose={() => {
            // Close details modal and clear selection
            setShowDetails(false);
            setViewingInventoryId(null);
            setInventoryDetails(null);
          }}
        />
      )}

      {showAdjustForm && (
        <WarehouseInventoryAdjustForm
          isOpen={showAdjustForm}
          inventory={adjustingInventory}
          onSubmit={handleAdjustSubmit}
          onCancel={() => {
            setShowAdjustForm(false);
            setAdjustingInventory(null);
          }}
        />
      )}

      {showTransferForm && (
        <WarehouseInventoryTransferForm
          isOpen={showTransferForm}
          onSubmit={handleTransferSubmit}
          onCancel={() => setShowTransferForm(false)}
        />
      )}

      {showImportModal && (
        <InventoryImportModal
          isOpen={showImportModal}
          importing={importing}
          importError={importError}
          importSuccess={importSuccess}
          onImport={handleImportInventory}
          onClose={() => {
            setShowImportModal(false);
            setImportError(null);
            setImportSuccess(false);
          }}
        />
      )}

      {canExportBySubcategory && showExportModal && (
        <WarehouseInventoryExportModal
          isOpen={showExportModal}
          loading={exportLoading}
          onExport={handleExportBySubcategory}
          onClose={() => setShowExportModal(false)}
        />
      )}

      {errorDialog && (
        <ConfirmDialog
          isOpen
          title={errorDialog.title}
          message={errorDialog.message}
          confirmLabel={errorDialog._confirmDiscard ? bulkStrings.discardConfirm : t('common.actions.ok')}
          cancelLabel={errorDialog._confirmDiscard ? t('common.actions.keepEditing') : null}
          confirmVariant={'danger'}
          loading={false}
          onConfirm={() => {
            if (errorDialog._confirmDiscard) {
              handleDiscardBulk();
            }
            setErrorDialog(null);
          }}
          onCancel={() => setErrorDialog(null)}
        />
      )}

      {bulkEditMode && (
        <div
          className="fixed bottom-0 left-0 right-0 z-40 border-t shadow-lg px-4 py-3"
          style={{
            backgroundColor: '#fff',
            borderColor: UI_COLORS.BORDERS.MEDIUM,
          }}
        >
          <div className="max-w-7xl mx-auto flex items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div
                className="w-2 h-8 rounded"
                style={{ backgroundColor: dirtySummary.cellCount > 0 ? '#3b82f6' : UI_COLORS.TEXT.MUTED }}
              />
              <div>
                <div className="text-sm font-semibold" style={{ color: UI_COLORS.TEXT.PRIMARY }}>
                  {bulkSaving
                    ? (bulkStrings.savingProgress)
                        .replace('{current}', String(bulkProgress.current))
                        .replace('{total}', String(bulkProgress.total))
                    : (bulkStrings.dirtyCount)
                        .replace('{count}', String(dirtySummary.cellCount))
                        .replace('{products}', String(dirtySummary.productCount))
                  }
                </div>
                <div className="text-xs" style={{ color: UI_COLORS.TEXT.MUTED }}>
                  {dirtySummary.hiddenProductCount > 0
                    ? (bulkStrings.hiddenHint).replace('{n}', String(dirtySummary.hiddenProductCount))
                    : (bulkStrings.footerHint)
                  }
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={handleDiscardBulk}
                disabled={bulkSaving}
                className="px-4 py-2 rounded-lg font-semibold border transition-colors disabled:opacity-50"
                style={{ borderColor: UI_COLORS.BORDERS.MEDIUM, color: UI_COLORS.TEXT.PRIMARY }}
              >
                {bulkStrings.discard}
              </button>
              <button
                onClick={handleBulkSave}
                disabled={bulkSaving || dirtySummary.cellCount === 0}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg font-semibold transition-colors disabled:opacity-50"
                style={{
                  backgroundColor: UI_COLORS.BUTTONS.PRIMARY.background,
                  color: UI_COLORS.BUTTONS.PRIMARY.text,
                }}
              >
                <FontAwesomeIcon icon={bulkSaving ? faSpinner : faSave} className={bulkSaving ? 'animate-spin' : ''} />
                {bulkStrings.saveAll}
              </button>
            </div>
          </div>
        </div>
      )}

      {deleteConfirm && (
        <ConfirmDialog
          isOpen
          title={deleteConfirm.title}
          message={deleteConfirm.message}
          confirmLabel={deleteConfirm.confirmLabel}
          cancelLabel={deleteConfirm.cancelLabel}
          confirmVariant={deleteConfirm.confirmVariant}
          loading={false}
          onConfirm={confirmDelete}
          onCancel={() => setDeleteConfirm(null)}
        />
      )}

      {/* Coil ledger for one coil-tracked SKU: enter, correct and write off individual
          coils, with a banner reconciling their total against the stock balance. */}
      {coilLedgerTarget && coilLedgerTarget.warehouse_id != null && (
        <CoilLedgerDrawer
          isOpen={!!coilLedgerTarget}
          onClose={() => setCoilLedgerTarget(null)}
          warehouseId={coilLedgerTarget.warehouse_id}
          warehouseName={coilLedgerTarget.warehouse_name}
          productSizeId={coilLedgerTarget.product_size_id}
          productName={coilLedgerTarget.product_name}
        />
      )}
    </div>
  );
};

export default WarehouseInventoryPage;